import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Chip,
  TextField,
  Button,
  Grid,
  Paper,
  Snackbar,
  Alert,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import {
  Phone,
  Person,
  Email,
  Message,
  Send,
  CheckCircle,
} from "@mui/icons-material";
import { sendCallbackEmail } from "../../services/emailService";
import styles from "./RequestCallback.module.css";

const RequestCallback = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await sendCallbackEmail(formData);

      setSnackbar({
        open: true,
        message: "Thank you! We will call you back within 24 hours.",
        severity: "success",
      });

      setFormData({
        name: "",
        email: "",
        phone: "",
        message: "",
      });
    } catch (error) {
      console.error("Email send error:", error);
      setSnackbar({
        open: true,
        message: "Something went wrong. Please try again or call us directly.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box className={styles.callbackSection}>
      <Container maxWidth="lg">
        <Grid container spacing={6} alignItems="center">
          {/* Left Side - Info */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Box className={styles.callbackInfo}>
              <Chip
                label="📞 Request a Call Back"
                className={styles.callbackChip}
                size="small"
              />
              <Typography
                variant="h2"
                className={styles.callbackTitle}
                sx={{
                  fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                }}
              >
                We're Here to <span className={styles.highlight}>Help</span>
              </Typography>
              <Typography variant="body1" className={styles.callbackSubtitle}>
                Have questions? Need a consultation? Fill in the form and our
                team will get back to you within 24 hours.
              </Typography>

              <Box className={styles.infoCards}>
                <Box className={styles.infoCard}>
                  <Box className={styles.infoIcon}>
                    <Phone sx={{ fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography style={{ color: "white" }} fontWeight={600}>
                      Call Us Directly
                    </Typography>
                    <Typography style={{ color: "white" }}>
                      +44 20 7123 4567
                    </Typography>
                  </Box>
                </Box>
                <Box className={styles.infoCard}>
                  <Box className={styles.infoIcon}>
                    <Email sx={{ fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography style={{ color: "white" }} fontWeight={600}>
                      Email Us
                    </Typography>
                    <Typography style={{ color: "white" }}>
                      waveserv2@gmail.com
                    </Typography>
                  </Box>
                </Box>
                <Box className={styles.infoCard}>
                  <Box className={styles.infoIcon}>
                    <CheckCircle sx={{ fontSize: 28 }} />
                  </Box>
                  <Box>
                    <Typography style={{ color: "white" }} fontWeight={600}>
                      Response Time
                    </Typography>
                    <Typography style={{ color: "white" }}>
                      Within 24 hours
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Right Side - Form */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} className={styles.callbackForm}>
              <Typography variant="h5" className={styles.formTitle}>
                Send Us a Message
              </Typography>
              <Typography variant="body2" className={styles.formSubtitle}>
                Fill in your details and we'll get back to you
              </Typography>

              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Full Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className={styles.formField}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person sx={{ color: "#8cc61d" }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className={styles.formField}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email sx={{ color: "#8cc61d" }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className={styles.formField}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Phone sx={{ color: "#8cc61d" }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Your Message"
                  name="message"
                  multiline
                  rows={4}
                  value={formData.message}
                  onChange={handleChange}
                  required
                  className={styles.formField}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Message sx={{ color: "#8cc61d" }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  className={styles.submitButton}
                  disabled={loading}
                  endIcon={!loading && <Send />}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Request Call Back"
                  )}
                </Button>

                <Typography variant="caption" className={styles.secureText}>
                  🔒 Your information is secure and will not be shared
                </Typography>
              </form>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RequestCallback;
